import React from 'react';
import { Card } from '@/components/ui/card';
import { TrendingDown, TrendingUp, CheckCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', amber: '#E8A020', slate: '#5C6B7A' };

const META = {
  below_market: { label: 'Below market', color: C.teal, Icon: TrendingDown,
    line: (d) => `This quote sits ${d}% under the market floor — strong win position, check adequacy.` },
  competitive: { label: 'Competitive', color: C.green, Icon: CheckCircle,
    line: () => 'This quote sits inside the expected market range for a risk of this size.' },
  above_market: { label: 'Above market', color: C.amber, Icon: TrendingUp,
    line: (d) => `This quote runs ${d}% over the market ceiling — expect price pressure from the client.` },
};

const fmt$ = (v) => `$${Math.round(v).toLocaleString('en-AU')}`;

export const MarketPositionCard = ({ mp }) => {
  const meta = META[mp.position] || META.competitive;
  const { Icon } = meta;
  const span = Math.max(mp.market_high * 1.25 - mp.market_low * 0.75, 1);
  const pos = Math.min(Math.max((mp.quote_total - mp.market_low * 0.75) / span, 0.02), 0.98);
  const lowPct = (mp.market_low * 0.25) / span;
  const bandPct = (mp.market_high - mp.market_low) / span;

  return (
    <Card className="p-4 rounded-sm bg-white" style={{ border: `1px solid ${meta.color}40`, borderLeft: `4px solid ${meta.color}` }} data-testid="market-position-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
          <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Market position</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: `${meta.color}15`, color: meta.color }} data-testid="market-position-chip">{meta.label}</span>
        </div>
        <span className="text-xs" style={{ color: C.slate }}>
          Market range {fmt$(mp.market_low)} – {fmt$(mp.market_high)}
        </span>
      </div>
      <div className="mt-3 relative h-6" data-testid="market-position-bar">
        <div className="absolute inset-x-0 top-2.5 h-1 rounded-full" style={{ background: 'rgba(136,150,168,0.18)' }} />
        <div className="absolute top-2.5 h-1 rounded-full" style={{ left: `${lowPct * 100}%`, width: `${bandPct * 100}%`, background: `${C.green}55` }} />
        <div className="absolute top-0.5 w-[3px] h-5 rounded-sm" style={{ left: `calc(${pos * 100}% - 1px)`, background: C.navy }} />
      </div>
      <div className="flex items-center justify-between text-xs mt-1">
        <span style={{ color: C.slate }}>{meta.line(mp.delta_pct)}</span>
        {mp.book && (
          <span className="shrink-0 ml-3" style={{ color: C.slate }} data-testid="market-position-book">
            Book median <span className="font-medium" style={{ color: C.navy }}>{fmt$(mp.book.median)}</span> · P{mp.book.percentile} of {mp.book.sample} similar policies
          </span>
        )}
      </div>
    </Card>
  );
};

export default MarketPositionCard;
